import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import Draggable from 'react-draggable';
import Box from '@mui/material/Box';
import '../../../assets/styles/BRandRBBItem.css';
import {calcInitialPosition} from '../../../util/calculateInitialItemValues';
import {ItemTypes} from '../util/itemTypes';
import {ArdItemMenu} from './ArdItemMenu';
import {ArdItemInput} from './ArdItemInput';
import {ArdDefaultBackground} from './ArdDefaultBackground';
import ArdMenuHandle from './ArdMenuHandle';
import ArdDangerBackground from './ArdDangerBackground';
import ArdDangerSignSelect from './ArdDangerSignSelect';

const DEFAULT_WIDTH = 128;
const DEFAULT_HEIGHT = 128;
function calcPosition(position, scale) {
  return {
    x: scale * (position.x - DEFAULT_WIDTH / 2),
    y: scale * (position.y - DEFAULT_HEIGHT / 2)
  };
}

const ArdItem = ({
  item,
  mapScale,
  broadcaster,
  parentData,
  setValueInMap,
  currentMap
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [position, setPosition] = useState({x: 0, y: 0});

  useEffect(() => {
    const calcHidden = item => {
      if (typeof item.data.hidden !== 'undefined') {
        return item.data.hidden.value;
      }
      return false;
    };
    setIsHidden(calcHidden(item));

    setPosition(calcPosition(calcInitialPosition(item, parentData), mapScale));
    setIsInitialized(true);
  }, []);

  const [locationLocked, setLocationLocked] = useState(true);

  const [isBeingDragged, setIsBeingIsBeingDragged] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const itemValue = item.data[item.type].value;

  const resetPosition = useCallback(() => {
    setPosition(calcPosition(item.data.defaultPosition, mapScale));
    setValueInMap(item, {x: {value: item.data.defaultPosition.x}});
    setValueInMap(item, {y: {value: item.data.defaultPosition.y}});
  }, [setPosition, item, setValueInMap, mapScale, broadcaster]);

  const handleLock = useCallback(
    v => {
      setLocationLocked(v);
    },
    [setLocationLocked]
  );
  const handleHide = useCallback(
    value => {
      setIsHidden(value);
      setValueInMap(item, {hidden: {value}});
    },
    [item, setIsHidden, setValueInMap]
  );

  const handleDragStart = useCallback(() => {
    if (!locationLocked) setIsBeingIsBeingDragged(true);
  }, [locationLocked, setIsBeingIsBeingDragged]);

  const handleDrag = useCallback(
    (e, position) => {
      if (locationLocked) return;
      setPosition(position);
    },
    [locationLocked]
  );

  const handleDragStop = useCallback(
    (e, position) => {
      if (locationLocked) return;

      setPosition(position);
      const newX = position.x / mapScale + DEFAULT_WIDTH * mapScale;
      const newY = position.y / mapScale + DEFAULT_HEIGHT * mapScale;
      setValueInMap(item, {x: {value: newX}});
      setValueInMap(item, {y: {value: newY}});

      setIsBeingIsBeingDragged(false);
    },
    [locationLocked, setValueInMap, mapScale, item]
  );

  const handleMenuToggleClicked = useCallback(
    e => {
      setShowMenu(e.target.checked);
    },
    [setShowMenu]
  );

  const content = useMemo(() => {
    if (showMenu) {
      return (
        <ArdItemMenu
          currentMap={currentMap.id}
          item={item}
          hidden={isHidden}
          locked={locationLocked}
          onReset={resetPosition}
          onLock={handleLock}
          onHide={handleHide}
          setShowMenu={setShowMenu}
        />
      );
    }
    if (isHidden) return <Fragment />;
    if ([ItemTypes.TEMPERATURE, ItemTypes.FOG].includes(item.type)) {
      return (
        <ArdItemInput
          currentMap={currentMap.id}
          item={item}
          data={{key: item.type, value: itemValue}}
          callback={setValueInMap}
          locationLocked={locationLocked}
        />
      );
    }
    if (item.type === ItemTypes.DANGER) {
      return (
        <ArdDangerSignSelect
          item={item}
          data={{key: item.type, value: itemValue}}
          callback={setValueInMap}
          locationLocked={locationLocked}
        />
      );
    }
    if (item.type === ItemTypes.WIND_DIRECTION) {
      return <Fragment />;
    }
    return <Fragment />;
  }, [
    item,
    itemValue,
    isHidden,
    locationLocked,
    showMenu,
    resetPosition,
    handleLock,
    handleHide,
    setValueInMap,
    currentMap.id
  ]);

  if (!isInitialized) return <Fragment />;

  return (
    <Draggable
      onStart={handleDragStart}
      onStop={handleDragStop}
      onDrag={handleDrag}
      position={position}
      disabled={Boolean(item.data.movable === false || locationLocked)}>
      <Box
        sx={{
          pointerEvents: 'all',
          width: 64,
          height: [ItemTypes.TEMPERATURE, ItemTypes.FOG].includes(item.type)
            ? 64
            : 96,
          display: 'flex',
          flexDirection: 'column',
          position: 'absolute',
          cursor: locationLocked
            ? 'default'
            : isBeingDragged
            ? 'grabbing'
            : 'grab'
        }}>
        {[ItemTypes.TEMPERATURE, ItemTypes.FOG].includes(item.type) ? (
          <ArdDefaultBackground
            item={item}
            value={itemValue}
            mapScale={mapScale}
            broadcaster={broadcaster}
            locationLocked={locationLocked}
            isHidden={isHidden}
          />
        ) : (
          <ArdDangerBackground
            itemValue={itemValue}
            itemType={item.type}
            isHidden={isHidden}
            locationLocked={locationLocked}
          />
        )}
        <ArdMenuHandle
          showMenu={showMenu}
          handleMenuToggleClicked={handleMenuToggleClicked}
          id={`${currentMap.id}_${item.id}__handle`}
        />
        {content}
      </Box>
    </Draggable>
  );
};

export default ArdItem;
